import React, { useEffect, useState } from "react";
import Modal from "../../components/Modal";
import { BusinessUnitFormData } from "../../interfaces/businessUnits";
import { createBusinessUnit, updateBusinessUnit } from "../../apis/businessUnits";

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editableId?: number | null;
  businessUnitData?: BusinessUnitFormData | null;
}

const AddBusinessUnitModal: React.FC<Props> = ({
  open,
  onClose,
  onSuccess,
  editableId,
  businessUnitData,
}) => {
  const defaultBusinessUnit: BusinessUnitFormData = {
    name: "",
    gstNumber: "",
    legalName: "",
    address: "",
    stateCode: "",
    tallyCompanyName: "",
    isDefault: false,
  };

  const [businessUnit, setBusinessUnit] = useState<BusinessUnitFormData>(defaultBusinessUnit);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const isEditMode = Boolean(editableId);

  useEffect(() => {
    if (!open) return;
    if (isEditMode && businessUnitData) {
      setBusinessUnit(businessUnitData);
    } else {
      setBusinessUnit(defaultBusinessUnit);
    }
    setErrors({});
  }, [open, isEditMode, businessUnitData]);

  const validateForm = () => {
    const e: Record<string, string> = {};
    if (!businessUnit.name.trim()) e.name = "* Name is required";
    if (!businessUnit.gstNumber.trim()) e.gstNumber = "* GST Number is required";
    if (!businessUnit.legalName.trim()) e.legalName = "* Legal Name is required";
    if (!businessUnit.address.trim()) e.address = "* Address is required";
    if (!businessUnit.stateCode.trim()) e.stateCode = "* State Code is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      if (isEditMode) {
        await updateBusinessUnit(editableId!, businessUnit);
      } else {
        await createBusinessUnit(businessUnit);
      }
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error saving business unit:", error);
    }
  };

  if (!open) return null;

  return (
    <Modal
      title={isEditMode ? "Edit Business Unit" : "Add Business Unit"}
      onClose={onClose}
      modalContent={
        <div className="space-y-4">
          <div>
            <label>Name</label>
            <input
              type="text"
              value={businessUnit.name}
              onChange={(e) => setBusinessUnit({ ...businessUnit, name: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
            />
            {errors.name && <span className="text-sm text-red-500">{errors.name}</span>}
          </div>

          <div>
            <label>GST Number</label>
            <input
              type="text"
              value={businessUnit.gstNumber}
              onChange={(e) => setBusinessUnit({ ...businessUnit, gstNumber: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
            />
            {errors.gstNumber && <span className="text-sm text-red-500">{errors.gstNumber}</span>}
          </div>

          <div>
            <label>Legal Name</label>
            <input
              type="text"
              value={businessUnit.legalName}
              onChange={(e) => setBusinessUnit({ ...businessUnit, legalName: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
            />
            {errors.legalName && <span className="text-sm text-red-500">{errors.legalName}</span>}
          </div>

          <div>
            <label>Address</label>
            <textarea
              value={businessUnit.address}
              onChange={(e) => setBusinessUnit({ ...businessUnit, address: e.target.value })}
              rows={3}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
            />
            {errors.address && <span className="text-sm text-red-500">{errors.address}</span>}
          </div>

          <div>
            <label>State Code</label>
            <input
              type="text"
              value={businessUnit.stateCode}
              onChange={(e) => setBusinessUnit({ ...businessUnit, stateCode: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
            />
            {errors.stateCode && <span className="text-sm text-red-500">{errors.stateCode}</span>}
          </div>

          <div>
            <label>Tally Company Name (optional)</label>
            <input
              type="text"
              value={businessUnit.tallyCompanyName || ""}
              onChange={(e) =>
                setBusinessUnit({ ...businessUnit, tallyCompanyName: e.target.value })
              }
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              id="is-default-business-unit"
              type="checkbox"
              checked={businessUnit.isDefault}
              onChange={(e) => setBusinessUnit({ ...businessUnit, isDefault: e.target.checked })}
              className="rounded border-gray-300"
            />
            <label htmlFor="is-default-business-unit">Set as default business unit</label>
          </div>

          <div className="flex justify-end gap-2">
            <button onClick={handleSave} className="px-3 py-2 bg-primary text-white rounded">
              {isEditMode ? "Update" : "Save"}
            </button>
            <button onClick={onClose} className="px-3 py-2 border rounded">
              Cancel
            </button>
          </div>
        </div>
      }
    />
  );
};

export default AddBusinessUnitModal;

